import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight, CheckCircle2, ShieldCheck, CalendarDays, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { buildBridgeLead } from '@/lib/eventsPortal';
import ConsentChecklist from '@/components/site/ConsentChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  clinic_name: '',
  notes: '',
  privacy_consent: false,
  email_newsletter_consent: false,
  whatsapp_newsletter_consent: false,
};

const TXT = {
  it: {
    pickTitle: 'Scegli il giorno e l\'orario',
    pickHelp: 'Le call si tengono ogni giovedì pomeriggio. Scegli lo slot che preferisci.',
    loading: 'Caricamento slot…',
    noSlots: 'Nessuno slot disponibile per le prossime settimane. Riprova più tardi o usa il form a fianco.',
    booked: 'Già prenotato',
    past: 'Passato',
    selected: 'Selezionato',
    selectFirst: 'Seleziona prima uno slot dal calendario',
    confirmTitle: 'Conferma i tuoi dati',
    confirmHelp: 'Riceverai una conferma via email + un promemoria il giorno dell\'incontro.',
    firstName: 'Nome *',
    lastName: 'Cognome *',
    email: 'Email *',
    phone: 'Telefono *',
    clinic: 'Studio / clinica (opzionale)',
    notes: 'Argomenti che vuoi affrontare (opzionale)',
    notesPh: 'Es. quanto tempo serve per la migrazione, formazione del team, percorso FSE 2.0…',
    privacy_a: 'Accetto la ',
    privacy_b: 'privacy policy',
    privacy_c: ' e il trattamento dei miei dati.',
    submit: 'Conferma l\'appuntamento',
    submitting: 'Conferma in corso…',
    success: 'Slot confermato! Controlla la tua email.',
    error: 'Qualcosa non ha funzionato. Riprova.',
    conflict: 'Lo slot è stato preso da qualcun altro. Scegli un altro orario.',
    successTitle: 'Appuntamento confermato',
    successDesc: 'Ti aspettiamo',
    successHelp: 'Ti abbiamo inviato una mail di conferma con tutti i dettagli. Riceverai un promemoria il giorno della call.',
    trust: 'Confidenziale · solo per il team Prime Vision · GDPR',
  },
  en: {
    pickTitle: 'Pick a day and time',
    pickHelp: 'Calls happen every Thursday afternoon. Pick the slot that suits you.',
    loading: 'Loading slots…',
    noSlots: 'No slots available in the next weeks. Try again later or use the form on the side.',
    booked: 'Booked',
    past: 'Past',
    selected: 'Selected',
    selectFirst: 'Pick a slot from the calendar first',
    confirmTitle: 'Confirm your details',
    confirmHelp: 'You\'ll get a confirmation email + a reminder the day of the call.',
    firstName: 'First name *',
    lastName: 'Last name *',
    email: 'Email *',
    phone: 'Phone *',
    clinic: 'Practice / clinic (optional)',
    notes: 'Topics you want to discuss (optional)',
    notesPh: 'E.g. migration time, team training, FSE 2.0 flow…',
    privacy_a: 'I accept the ',
    privacy_b: 'privacy policy',
    privacy_c: ' and the processing of my data.',
    submit: 'Confirm appointment',
    submitting: 'Confirming…',
    success: 'Slot confirmed! Check your email.',
    error: 'Something went wrong. Please retry.',
    conflict: 'That slot was just taken. Pick another time.',
    successTitle: 'Appointment confirmed',
    successDesc: 'See you',
    successHelp: 'We sent you a confirmation email with all the details. You\'ll receive a reminder on the day of the call.',
    trust: 'Confidential · Prime Vision team only · GDPR',
  },
};

export const SlotPicker = () => {
  const { locale } = useLanguage();
  const c = TXT[locale] || TXT.it;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIso, setSelectedIso] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // booking object

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/booking/slots`);
      setData(r.data);
    } catch {
      setData({ days: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const validate = () => {
    const e = {};
    if (!selectedIso) e._slot = c.selectFirst;
    if (!form.first_name.trim()) e.first_name = '!';
    if (!form.last_name.trim()) e.last_name = '!';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = '!';
    if (!form.phone.trim()) e.phone = '!';
    if (!form.privacy_consent) e.privacy_consent = '!';
    if (!form.email_newsletter_consent) e.email_newsletter_consent = '!';
    if (!form.whatsapp_newsletter_consent) e.whatsapp_newsletter_consent = '!';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      if (errors._slot) toast.error(errors._slot);
      return;
    }
    setSubmitting(true);
    try {
      let slotLabel = '';
      try {
        for (const day of (data?.days || [])) {
          const s = (day.slots || []).find((x) => x.iso === selectedIso);
          if (s) { slotLabel = `${day.label_full}${s.label ? ` · ${s.label}` : ''}`; break; }
        }
      } catch { /* label is cosmetic */ }
      const r = await axios.post(`${API}/bridge/booking`, buildBridgeLead(form, 'call', {
        clinic_name: form.clinic_name.trim(),
        notes: form.notes.trim(),
        slot_iso: selectedIso,
        slot_label: slotLabel || undefined,
        locale,
      }));
      setDone({ ...(r.data || {}), slot_label_full: slotLabel });
      toast.dismiss();
      toast.success(c.success);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(c.conflict);
        await fetchSlots();
        setSelectedIso(null);
      } else {
        toast.error(c.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // SUCCESS state
  if (done) {
    return (
      <div data-testid="pv-slotpicker-success" className="relative bg-white rounded-3xl p-8 md:p-12 border border-emerald-300 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)] overflow-hidden">
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-emerald-100 border border-emerald-300 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-700 mb-2 font-mono">
            {c.successTitle}
          </div>
          <h3 className="font-heading font-bold text-pv-ink text-[26px] md:text-[30px] leading-tight">
            {c.successDesc} {done.slot_label_full && (
              <span className="text-pv-teal">{done.slot_label_full}</span>
            )}
          </h3>
          <p className="mt-4 text-slate-600 text-[14.5px] leading-relaxed max-w-md">
            {c.successHelp}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pv-slotpicker" className="relative bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)] overflow-hidden">
      <div className="absolute -top-32 -right-20 w-64 h-64 rounded-full bg-pv-teal/12 blur-3xl pointer-events-none" />

      <div className="relative">
        {/* === STEP 1 — pick slot === */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-4 h-4 text-pv-teal" />
            <h3 className="font-heading font-bold text-pv-ink text-[16px]">{c.pickTitle}</h3>
          </div>
          <p className="text-[13px] text-slate-600 leading-snug">{c.pickHelp}</p>
        </div>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-[13px] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {c.loading}
          </div>
        ) : data && data.days && data.days.length > 0 ? (
          <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1 -mr-1">
            {data.days.map((day) => {
              const available = day.slots.filter((s) => s.available).length;
              return (
                <div key={day.date} className="border border-slate-200 rounded-2xl p-3.5 bg-gradient-to-br from-white to-slate-50">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="text-[14px] font-bold text-pv-ink capitalize">{day.label_full}</div>
                    <span className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded-full ${available > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {available} disponibili
                    </span>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {day.slots.map((s) => {
                      const isSelected = selectedIso === s.iso;
                      const isDisabled = !s.available;
                      const cls = isSelected
                        ? 'bg-pv-orange text-white border-pv-orange shadow-[0_6px_16px_-6px_rgba(255,107,53,0.5)]'
                        : isDisabled
                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                        : 'bg-white text-pv-ink border-slate-200 hover:border-pv-teal hover:bg-pv-mist';
                      return (
                        <button
                          key={s.iso}
                          type="button"
                          disabled={isDisabled}
                          data-testid={`pv-slot-${s.iso}`}
                          onClick={() => {
                            setSelectedIso(s.iso);
                            setErrors((er) => ({ ...er, _slot: undefined }));
                          }}
                          className={`relative inline-flex items-center justify-center gap-1 px-2 py-2.5 rounded-xl text-[12.5px] font-bold border transition-all ${cls}`}
                          title={isDisabled ? (s.past ? c.past : c.booked) : ''}
                        >
                          {!isDisabled && <Clock className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-slate-400'}`} />}
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center text-slate-400 text-[13px]">{c.noSlots}</div>
        )}

        {/* === WhatsApp helper · custom appointment === */}
        <div
          data-testid="pv-slot-whatsapp-helper"
          className="mt-5 relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 p-4 md:p-5"
        >
          <div className="flex items-start gap-3.5">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-[#25D366] flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(37,211,102,0.55)]">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current" aria-hidden="true">
                <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03ZM12.05 20.13h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 1 1 6.99 3.86Zm4.51-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23a7.49 7.49 0 0 1-1.38-1.72c-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.07-.1-.23-.16-.48-.29Z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-pv-ink text-[14px] leading-tight">
                {locale === 'en'
                  ? "Can't find a time? Need a custom slot?"
                  : "Non trovi un orario? Hai esigenze specifiche?"}
              </div>
              <div className="text-[12.5px] text-slate-600 leading-snug mt-1">
                {locale === 'en'
                  ? 'Message us on WhatsApp and we\'ll set up a slot tailored to you.'
                  : 'Scrivici su WhatsApp e ti fissiamo un appuntamento su misura.'}
              </div>
              <a
                href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20fissare%20un%20appuntamento%20Prime%20Vision%20su%20misura."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pv-slot-whatsapp-cta"
                className="pv-btn-whatsapp pv-btn-sm mt-3"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                  <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03Z"/>
                </svg>
                +39 352 052 6856
              </a>
            </div>
          </div>
        </div>

        {/* === STEP 2 — confirm details === */}
        <form onSubmit={submit} className="mt-6 pt-6 border-t border-slate-200" noValidate>
          <div className="mb-4">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">2. {c.confirmTitle}</div>
            <p className="text-[12.5px] text-slate-500 leading-snug">{c.confirmHelp}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <PField id="sp_fn" label={c.firstName} testId="pv-slot-first-name" value={form.first_name} onChange={onChange('first_name')} error={errors.first_name} />
            <PField id="sp_ln" label={c.lastName} testId="pv-slot-last-name" value={form.last_name} onChange={onChange('last_name')} error={errors.last_name} />
            <PField id="sp_em" label={c.email} type="email" testId="pv-slot-email" value={form.email} onChange={onChange('email')} error={errors.email} />
            <PField id="sp_ph" label={c.phone} type="tel" testId="pv-slot-phone" value={form.phone} onChange={onChange('phone')} error={errors.phone} />
            <div className="sm:col-span-2">
              <PField id="sp_cl" label={c.clinic} testId="pv-slot-clinic" value={form.clinic_name} onChange={onChange('clinic_name')} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[12.5px] font-bold text-slate-700 mb-1.5" htmlFor="sp_notes">{c.notes}</label>
              <textarea
                id="sp_notes"
                data-testid="pv-slot-notes"
                rows={3}
                value={form.notes}
                onChange={onChange('notes')}
                placeholder={c.notesPh}
                className="pv-input w-full resize-none"
              />
            </div>
            <div className="sm:col-span-2 mt-1">
              <ConsentChecklist
                form={form}
                onChange={onChange}
                errors={errors}
                testIdPrefix="pv-slot"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="pv-slot-submit"
            className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-pv-orange hover:bg-pv-orangeDeep text-white font-bold text-[14.5px] shadow-[0_18px_40px_-12px_rgba(255,107,53,0.55)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {c.submitting}
              </>
            ) : (
              <>
                {c.submit}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <ShieldCheck className="w-3 h-3 text-pv-teal" />
            {c.trust}
          </div>
        </form>
      </div>
    </div>
  );
};

const PField = ({ id, label, type = 'text', testId, value, onChange, error }) => (
  <div>
    <label htmlFor={id} className={`block text-[12.5px] font-bold mb-1.5 ${error ? 'text-red-600' : 'text-slate-700'}`}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      data-testid={testId}
      value={value}
      onChange={onChange}
      className={`pv-input w-full ${error ? '!border-red-400' : ''}`}
    />
  </div>
);

export default SlotPicker;
